'use client';

import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import Link from 'next/link';

import { PASSWORD_REGEX } from 'lib/constants';
import { Trans } from 'src/i18n/Trans';
import { useTranslation } from 'src/i18n/client';

import { fetchJson } from 'lib/fetch-json';
import useUser from 'src/hooks/useUser';
import Button from 'src/components/Button';
import FormError from 'src/components/FormError';
import toast from 'src/components/TailwindToaster';
import { myAccountUrl } from 'src/services/route-utils';
import PasswordInput from 'src/components/PasswordInput';

export default function PageForm({ locale, searchParams }) {
    const { t } = useTranslation(locale, 'common');
    const [loading, setLoading] = useState(false);

    // here we just check if user is already logged in and redirect to profile
    const { setLoadUser } = useUser({ redirectTo: myAccountUrl(), redirectIfFound: true });
    const {
        formState: { errors: _errors },
        handleSubmit,
        register,
        getValues,
    } = useForm();
    const errors: any = _errors;
    const { identifier, email } = searchParams || {};

    const fatalError = !identifier && !email ? 'invalidRequest' : null;

    const onSubmit = async (data: any) => {
        setLoading(true);
        const body = {
            identifier,
            email,
            password: data.password,
            'password-repeat': data.passwordRepeat,
        };

        try {
            await fetchJson('/json/activate-account', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            await setLoadUser();
            toast.success(t('activate.success'));
        } catch (error: any) {
            toast.error(t('activate.error'));
            setLoading(false);
        }
    };

    return (
        <div className="mx-auto text-center md:w-4/5 xl:w-2/3">
            <form noValidate onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
                <h1 className="text-5xl lg:text-6xl">{t('activate.heading')}</h1>
                <p className="my-4">{t('activate.description')}</p>

                {fatalError ? (
                    <div className="text-danger">{t(fatalError)}</div>
                ) : (
                    <>
                        <div>
                            <PasswordInput
                                placeholder={t('activate.passwordPlaceholder')}
                                className="form-control"
                                disabled={loading}
                                autoFocus
                                {...register('password', {
                                    required: t('activate.passwordRequired'),
                                    minLength: {
                                        value: 8,
                                        message: t('activate.passwordMinLength'),
                                    },
                                    pattern: {
                                        value: PASSWORD_REGEX,
                                        message: t('activate.passwordInvalid'),
                                    },
                                })}
                            />
                            <FormError error={errors.password} />
                        </div>
                        <div>
                            <PasswordInput
                                placeholder={t('activate.passwordRepeatPlaceholder')}
                                disabled={loading}
                                {...register('passwordRepeat', {
                                    validate: (value) =>
                                        value === getValues('password') || t('activate.passwordRepeatMismatch'),
                                })}
                            />
                            <FormError error={errors.passwordRepeat} />
                        </div>
                        <div className="text-left">
                            <label className="flex items-start gap-2">
                                <input
                                    className="mt-1"
                                    type="checkbox"
                                    disabled={loading}
                                    {...register('terms', {
                                        required: t('activate.termsRequired'),
                                    })}
                                />
                                <span>
                                    <Trans
                                        t={t}
                                        i18nKey="common:activate.termsLabel"
                                        components={{
                                            link: (
                                                <Link
                                                    className="text-primary underline hover:no-underline"
                                                    href="/termeni-si-conditii"
                                                />
                                            ),
                                        }}
                                    />
                                </span>
                            </label>
                            <FormError error={errors.terms} />
                        </div>

                        <Button variant="primary" type="submit" className="block" disabled={loading} loading={loading}>
                            {t('activate.button')}
                        </Button>
                    </>
                )}
            </form>
        </div>
    );
}
